import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { TID } from "@/lib/testIds";

const OUTCOMES = [
  { k: "01", t: "More family time", d: "A space that invites people in, instead of pushing them out." },
  { k: "02", t: "A more valuable home", d: "A kitchen people remember after the inspection ends." },
  { k: "03", t: "Less daily friction", d: "Better light. Better flow. Fewer small frustrations." },
];

const IMG =
  "https://images.pexels.com/photos/5082055/pexels-photo-5082055.jpeg?auto=compress&cs=tinysrgb&w=2400";

export default function Emotional() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [80, -80]);

  return (
    <section
      id="emotional"
      data-testid={TID.emotional.root}
      ref={ref}
      className="relative bg-cream py-24 md:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <p className="text-[11px] md:text-[12px] tracking-[0.32em] uppercase text-ink mb-10">
          Chapter One — The Feeling
        </p>
        <h2 className="font-display font-light text-[10vw] md:text-[5.6vw] leading-[0.98] tracking-tightest text-charcoal max-w-5xl">
          Imagine walking into{" "}
          <span className="italic text-brassDark">this</span> every morning.
        </h2>
        <p className="mt-8 md:mt-10 max-w-xl text-ink text-lg md:text-xl leading-relaxed">
          Beautiful spaces change how a home feels. The light shifts. The
          mornings slow down. People linger.
        </p>
      </div>

      <div className="relative mt-16 md:mt-24 overflow-hidden">
        <motion.img
          style={{ y }}
          src={IMG}
          alt="Family enjoying a warm modern kitchen at breakfast"
          className="w-full h-[60vh] md:h-[85vh] object-cover"
        />
      </div>

      <div className="mx-auto max-w-[1400px] px-6 md:px-12 mt-20 md:mt-28">
        <div className="grid md:grid-cols-12 gap-y-12 md:gap-x-12">
          <div className="md:col-span-4">
            <p className="text-[11px] tracking-[0.32em] uppercase text-ink">
              What changes
            </p>
            <h3 className="mt-6 font-display text-3xl md:text-4xl text-charcoal leading-tight">
              The home you walk into stops feeling like a house.
            </h3>
          </div>
          <ul className="md:col-span-8 md:pl-12 md:border-l border-stone divide-y divide-stone">
            {OUTCOMES.map((o) => (
              <motion.li
                key={o.k}
                data-testid={TID.emotional.outcome}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="py-8 grid grid-cols-12 gap-6 items-baseline"
              >
                <span className="col-span-2 font-display text-brassDark text-sm tracking-[0.22em]">
                  {o.k}
                </span>
                <h4 className="col-span-10 md:col-span-4 font-display text-2xl md:text-3xl text-charcoal">
                  {o.t}
                </h4>
                <p className="col-span-12 md:col-span-6 text-ink leading-relaxed">
                  {o.d}
                </p>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
